import React, { useEffect, useState } from 'react';
import { buildApiUrl, config, tenantApiPrefix } from '../config';

export interface TenantBranding {
  name: string;
  logoUrl: string;
  logoType?: 'icon' | 'wordmark';
  faviconUrl?: string;
  sidebarPosition?: 'left' | 'right';
  defaultHomePage?: 'chat' | 'dashboards' | 'spaces' | 'copilot';
  showLegacyVisualizer?: boolean;
  colors: {
    primary: {
      100: string;
      200: string;
      300: string;
      400: string;
      500: string;
    };
    semantic: {
      success: string;
      warning: string;
      error: string;
    };
    charts: string[];
  };
}

// Global reset function that can be called from clearAuthStorage
let globalResetBranding: (() => void) | null = null;

export function resetBrandingOnLogout() {
  if (globalResetBranding) {
    globalResetBranding();
  }
}

export function useBranding() {
  const [branding, setBranding] = useState<TenantBranding | null>(null);
  const [loading, setLoading] = useState(true);
  const [tenantId, setTenantId] = useState<string | null>(null);
  // Bumping `refreshKey` forces a re-fetch without changing tenant. Used by the
  // Branding settings page to hot-apply colour/logo changes without
  // `window.location.reload()` (which would torch any in-progress chat).
  const [refreshKey, setRefreshKey] = useState(0);

  // Watch for tenant changes in localStorage (login/logout)
  useEffect(() => {
    const checkTenant = () => {
      const tenantStr = localStorage.getItem('klaris_tenant');
      if (tenantStr) {
        try {
          const tenant = JSON.parse(tenantStr);
          const newTenantId = tenant?.tenant_id;
          if (newTenantId !== tenantId) {
            // Clear old branding immediately when tenant changes
            setBranding(null);
            setTenantId(newTenantId);
          }
        } catch {
          setTenantId(null);
        }
      } else {
        setTenantId(null);
      }
    };

    // Check immediately
    checkTenant();

    // Poll for changes more frequently (100ms for faster detection)
    const interval = setInterval(checkTenant, 100);
    
    return () => clearInterval(interval);
  }, [tenantId]);

  // Tab title: tenant name from API when available; otherwise build-time / default from config (onboarding, login, etc.)
  useEffect(() => {
    if (branding?.name) {
      document.title = branding.name;
    } else if (!loading) {
      document.title = config.brandName;
    }
  }, [branding?.name, loading]);

  // Fetch branding when tenant changes
  useEffect(() => {
    async function fetchBranding() {
      // Do not set loading=true here. That would unmount the entire app (ThemeProvider
      // shows a full-screen spinner), which resets in-memory route state - e.g. after
      // OTP verify we write klaris_tenant + JWT while still on /login; a refetch would
      // flash the email step until onboarding session resume completes.
      try {
        const token = localStorage.getItem('klaris_jwt');
        const tenantStr = localStorage.getItem('klaris_tenant');
        
        if (!token || !tenantStr) {
          setBranding(null);
          setLoading(false);
          return;
        }

        const tenant = JSON.parse(tenantStr);
        const currentTenantId = tenant?.tenant_id;

        if (!currentTenantId) {
          setBranding(null);
          setLoading(false);
          return;
        }

        const response = await fetch(buildApiUrl(`${tenantApiPrefix(currentTenantId)}/branding`), {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          cache: 'no-store',
        } as RequestInit);

        if (response.ok) {
          const brandingData = await response.json();

          if (brandingData) {
            setBranding(brandingData);
            
            // Update favicon: default to logo if faviconUrl missing
            const faviconHref: string | undefined = brandingData.faviconUrl || brandingData.logoUrl;
            if (faviconHref) {
              const link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
              if (link) {
                link.href = faviconHref;
              } else {
                const newLink = document.createElement('link');
                newLink.rel = 'icon';
                newLink.href = faviconHref;
                document.head.appendChild(newLink);
              }
            }
            
          }
        }
      } catch (error) {
        console.error('Failed to load branding:', error);
      } finally {
        setLoading(false);
      }
    }

    if (tenantId) {
      fetchBranding();
    } else {
      setBranding(null);
      setLoading(false);
    }
  }, [tenantId, refreshKey]);

  const refresh = React.useCallback(() => {
    setRefreshKey((k: number) => k + 1);
  }, []);

  // Register global reset function
  React.useEffect(() => {
    globalResetBranding = () => {
      // Clear branding state
      setBranding(null);
      
      // Reset document title to default
      document.title = config.brandName;
      
      // Reset favicon to default
      const link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
      if (link) {
        link.href = config.faviconUrl;
      }
    };
    
    return () => {
      globalResetBranding = null;
    };
  }, []);

  return { branding, loading, refresh };
}
